import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type Chat, useChat } from '@ai-sdk/react';
import { useQueryClient } from '@tanstack/react-query';
import type { FileUIPart } from 'ai';
import { useSnackbar } from 'notistack';
import auth from '~/auth';

import { Alert, Box, Button, Link, Skeleton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { type LensFiltered, applyChatLensFilters } from '~/pages/LeLens/LensFilterReceipt';

import { REPORTING_LE_DATA_ASK_PATH } from '~/consts/reportingLeDataApi';
import { reportingLeLensPath } from '~/consts/reportingLeLensApi';

import { useAbortReportingLeDataSession } from '~/hooks/apis/reporting/useAbortReportingLeDataSession';
import type { LensCreateResumableSession } from '~/hooks/apis/reporting/useGetLensCreateSessions';
import {
  reportingLeDataSessionsQueryKeys,
  useRecentReportingLeDataSessions,
} from '~/hooks/apis/reporting/useRecentReportingLeDataSessions';
import { useSteerReportingLeDataSession } from '~/hooks/apis/reporting/useSteerReportingLeDataSession';

import type { LeDataContextBlock } from '~/services/ReportingAiAskService';
import { request } from '~/services/common';

import type { LeDataImageAttachment, LeDataSessionVisibility, LensIntent, LensScopeBlockPayload } from '~/types/leData';
import type { Lens, LensPlanMode, LensResponse } from '~/types/leLens';

import { resolveReportingLeDataUrl } from '~/utils/resolveReportingLeDataUrl';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import CardAskEmptyState from '../chat/CardAskEmptyState';
import DatasetAskIntro, { type DatasetAskContext } from '../chat/DatasetAskIntro';
import LensCreateEmptyState from '../chat/LensCreateEmptyState';
import { stripInternalSessionContext } from '../chat/SessionHistoryPanel';
import { latestCreatedReceiptId } from '../chat/createdLensReceipt';
import type { DashboardDestinations } from '../chat/dashboardDestinations';
import { isAbortEndpointMissing, isSteerConflict } from '../chat/httpStatus';
import type { LensLaunchpadContext } from '../chat/lensContext';
import { RICHARD_MICROCOPY } from '../chat/microcopy';
import { resolveLensIntentForTurn } from '../chat/resolveLensIntentForTurn';
import { NEW_SESSION_MODE, type ShellRequestKind } from '../chat/sessionDefaults';
import { isLongChatSnoozed, snoozeLongChat } from '../chat/threadLength';
import { type MessageEntranceTracker, messageEntranceSx, resolveMessageEntrance } from '../chat/uiTokens';
import useAnswerSpeech from '../chat/useAnswerSpeech';
import { useExportPdf } from '../chat/useExportPdf';
import type { PageBridge, PageRequest } from '../page/pageBridge';
import { PageDelivery } from '../page/pageDelivery';

import AssistantTurnView, { type LensHandlers } from './AssistantTurnView';
import EmptyState from './EmptyState';
import FollowUps, { suggestedFollowUps } from './FollowUps';
import JumpToLatestButton from './JumpToLatestButton';
import LongChatNotice from './LongChatNotice';
import ThreadBar from './ThreadBar';
import TurnFaultAlert, { type TurnFault } from './TurnFaultAlert';
import UiStreamComposer, { type ComposerScope } from './UiStreamComposer';
import UserTurn, { type UserTurnEvidence } from './UserTurn';
import type { TurnStatus } from './WorkSummary';
import { type AskChatData, type AskChatFinish, createAskChat, statusTextOf } from './askChat';
import { type AskTurnOptions, createAskTransport } from './askTransport';
import type { AskUIMessage, StreamedLens } from './askUiMessage';
import { describeChatFailure } from './chatFailure';
import { imagePartOf } from './imageParts';
import { type LoadedSessionFacts, currentContextChars, longChatNoticeState, resumedChatDay } from './longChat';
import { SPINE_MARKER, SPINE_WIDTH, SPINE_X, paneTokens } from './paneTokens';
import {
  type AssistantTurn,
  conversationMessages,
  latestLensBlockIds,
  latestSessionId,
  projectAssistantTurn,
  userMessageText,
} from './projectTurn';
import { canRetryLatestTurn } from './retryHistory';
import { useFollowLatest } from './useFollowLatest';
import { useImageAttachments } from './useImageAttachments';
import { historyAwaitsReply, useUiStreamSessionHistory } from './useUiStreamSessionHistory';

const STREAM_THROTTLE_MS = 150;
const CUT_OFF_MESSAGE = 'The connection dropped before askRichard finished, so this answer may be incomplete.';
const HISTORY_EXPIRED_MESSAGE = 'askRichard may still be working on this answer. Reload to see if it finished.';
const STOPPED_MESSAGE = 'You stopped this answer.';
const EXPORT_ON_OPEN_RENDER_DELAY_MS = 1000;

type ControlledDraft = {
  requestId: number;
  text: string;
  images?: Array<LeDataImageAttachment>;
  context?: ReadonlyArray<LeDataContextBlock>;
  autoSend?: boolean;
  preserveCurrentSession?: boolean;
  lensIntent?: LensIntent;
};

interface UiStreamChatProps extends Pick<
  React.ComponentProps<typeof AssistantTurnView>,
  'lensSaveTarget' | 'forcedReplacementCard' | 'cardApply'
> {
  pageBridge?: PageBridge;
  toolbarActions?: React.ReactNode;
  toolbarLeadingAction?: React.ReactNode;
  historyPanel?: React.ReactNode;
  controlledSessionId?: string | null;
  exportOnOpenSessionId?: string | null;
  controlledNewChatRequestId?: number | null;
  controlledDraft?: ControlledDraft | null;
  claimShellRequest?: (kind: ShellRequestKind, requestId: number) => boolean;
  turnContext?: ReadonlyArray<LeDataContextBlock>;
  onSessionChange?: (sessionId: string | null) => void;
  onStreamingChange?: (streaming: boolean) => void;
  onSavedLens?: (lens: Lens) => void;
  onBuildDashboard?: (
    brief: string,
    context?: { hasExecutedResults: boolean; newLens?: boolean; sourceQuestion?: string },
  ) => void;
  onLensCreated?: (lensId: string) => void;
  autoOpenCreatedLens?: boolean;
  onLensStreamed?: (lens: StreamedLens) => void;
  dashboardDestinations?: DashboardDestinations;
  prominentLensSave?: boolean;
  debug?: boolean;
  sessionOrigin?: 'lens_create';
  creationSpaceId?: string;
  createEmptyState?: boolean;
  createSourceAnchorId?: string;
  createResumeSessions?: ReadonlyArray<LensCreateResumableSession>;
  onResumeCreateSession?: (sessionId: string) => void;
  onOpenLens?: (lensId: string) => void;
  composerPlaceholder?: string;
  lensContext?: LensLaunchpadContext;
  lensContextPending?: boolean;
  subjectChip?: string;
  /** Introduction for a snapshot-backed pane; transport and composer remain native. */
  emptyState?: React.ReactNode;
  /**
   * 'pane': the Lens sidebar. The card scope is a removable pill in the composer, the empty
   * state offers starter questions about the selected card, and follow-ups appear after an
   * answer. 'page' (default): the full-page Ask view, which keeps its thread bar and dataset scope.
   */
  variant?: 'page' | 'pane';
  /** Drops the card scope so the next question is about the whole Lens. */
  onClearScope?: () => void;
  /** Shown under the first turn of a session a card refresh started, whose text no longer carries the readings. */
  firstTurnEvidence?: UserTurnEvidence;
  datasetContext?: DatasetAskContext;
  onBackToDataset?: () => void;
  onContinueInNewChat?: (question: string) => void;
  /**
   * The surface's own new-chat action, offered by the long-chat notices: the Lens panel's
   * startNewChat (handleNewLensChat) or the full page's handleNewThread. No notices without it.
   */
  onStartNewChat?: () => void;
  /** The Lens panel reopened this chat on its own rather than the reader picking it from history. */
  sessionAutoResumed?: boolean;
}

type TurnEnding = 'stopped' | 'cut-off';
type TurnTiming = { startedAt: number; finishedAt?: number };

const PENDING_TURN: AssistantTurn = { id: 'pending', blocks: [], timeline: [], steers: [], hasAnswerText: false };

function parseStamp(stamp: string | undefined): number | undefined {
  const parsed = stamp ? Date.parse(stamp) : NaN;
  return Number.isFinite(parsed) ? parsed : undefined;
}

export default function UiStreamChat({
  pageBridge,
  toolbarActions,
  toolbarLeadingAction,
  historyPanel,
  controlledSessionId,
  exportOnOpenSessionId = null,
  controlledNewChatRequestId = null,
  controlledDraft = null,
  claimShellRequest,
  turnContext,
  onSessionChange,
  onStreamingChange,
  onSavedLens,
  onBuildDashboard,
  onLensCreated,
  autoOpenCreatedLens = false,
  onLensStreamed,
  dashboardDestinations,
  prominentLensSave = false,
  debug = false,
  sessionOrigin,
  creationSpaceId,
  createEmptyState = false,
  createSourceAnchorId,
  createResumeSessions,
  onResumeCreateSession,
  onOpenLens,
  composerPlaceholder,
  lensContext,
  lensContextPending = false,
  lensSaveTarget,
  forcedReplacementCard,
  cardApply,
  subjectChip,
  emptyState,
  variant = 'page',
  onClearScope,
  firstTurnEvidence,
  datasetContext,
  onBackToDataset,
  onContinueInNewChat,
  onStartNewChat,
  sessionAutoResumed = false,
}: UiStreamChatProps) {
  const theme = useTheme();
  const pane = paneTokens(theme);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: steerSession } = useSteerReportingLeDataSession();
  const { mutateAsync: abortSession } = useAbortReportingLeDataSession();

  const pageBridgeRef = useRef(pageBridge);
  pageBridgeRef.current = pageBridge;
  const pageRequests = useRef(new PageDelivery());

  const transport = useMemo(
    () =>
      createAskTransport({
        api: resolveReportingLeDataUrl(REPORTING_LE_DATA_ASK_PATH),
        getAccessToken: () => auth.getAccessToken(),
      }),
    [],
  );
  const [threadSessionId, setThreadSessionId] = useState<string | null>(null);
  const [openingSessionId, setOpeningSessionId] = useState<string | null>(null);
  const [loadedHistorySessionId, setLoadedHistorySessionId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [endings, setEndings] = useState<Record<string, TurnEnding>>({});
  const [timings, setTimings] = useState<Record<string, TurnTiming>>({});
  const [liveStatus, setLiveStatus] = useState<string | null>(null);
  const [pendingSteers, setPendingSteers] = useState<Array<string>>([]);
  const [stopping, setStopping] = useState(false);
  const [confirmingHistory, setConfirmingHistory] = useState(false);
  const historyConfirmationRef = useRef<symbol | null>(null);
  const [pendingTurn, setPendingTurn] = useState<{ startedAt: number; afterMessageId?: string } | null>(null);
  const speech = useAnswerSpeech();
  const { stopSpeech } = speech;
  const images = useImageAttachments();
  const { replace: replaceImages } = images;
  const [visibilityBySession, setVisibilityBySession] = useState<Record<string, LeDataSessionVisibility>>({});
  const recordVisibility = useCallback((forSessionId: string, next: LeDataSessionVisibility) => {
    setVisibilityBySession((prev) => ({ ...prev, [forSessionId]: next }));
  }, []);
  const [readOnly, setReadOnly] = useState(false);
  const [loadedSession, setLoadedSession] = useState<LoadedSessionFacts | null>(null);
  const [snoozedSessionId, setSnoozedSessionId] = useState<string | null>(null);
  const { exporting, exportConversation } = useExportPdf();
  const threadView = !lensContext && !sessionOrigin;
  const { data: recentSessions } = useRecentReportingLeDataSessions(threadView);

  const creationNavigationRef = useRef({ enabled: autoOpenCreatedLens, onLensCreated });
  creationNavigationRef.current = { enabled: autoOpenCreatedLens, onLensCreated };
  const activeChatRef = useRef<Chat<AskUIMessage> | null>(null);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleFinish = useCallback(
    ({ message, isAbort, isDisconnect, isError, finishReason }: AskChatFinish) => {
      const finishedAt = Date.now();
      setTimings((prev) => ({
        ...prev,
        [message.id]: { startedAt: prev[message.id]?.startedAt ?? finishedAt, finishedAt },
      }));
      setPendingSteers([]);
      setStopping(false);
      void queryClient.invalidateQueries({ queryKey: reportingLeDataSessionsQueryKeys.all });
      if (!isAbort && !isDisconnect && !isError && finishReason && creationNavigationRef.current.enabled) {
        const created = [...message.parts].reverse().find((part) => part.type === 'data-lens_created');
        if (created?.type === 'data-lens_created') {
          const originatingChat = activeChatRef.current;
          void request(resolveReportingLeLensUrl(reportingLeLensPath(created.data.lensId)))
            .then((response) => {
              const saved = response as LensResponse;
              const navigation = creationNavigationRef.current;
              if (
                mountedRef.current &&
                originatingChat === activeChatRef.current &&
                originatingChat?.messages[originatingChat.messages.length - 1]?.id === message.id &&
                navigation.enabled &&
                saved.lens.id === created.data.lensId
              ) {
                navigation.onLensCreated?.(saved.lens.id);
              }
            })
            .catch(() => undefined);
        }
      }
      if (isAbort) {
        setEndings((prev) => ({ ...prev, [message.id]: 'stopped' }));
      } else if (isDisconnect || (!isError && !finishReason && message.role === 'assistant')) {
        setEndings((prev) => ({ ...prev, [message.id]: 'cut-off' }));
      }
    },
    [queryClient],
  );
  const onLensStreamedRef = useRef(onLensStreamed);
  onLensStreamedRef.current = onLensStreamed;
  const handleData = useCallback((part: AskChatData) => {
    if (part.type === 'data-page_request') {
      const request = part.data as unknown as PageRequest;
      const bridge = pageBridgeRef.current;
      if (!bridge) return;
      const reply = pageRequests.current.deliver(bridge, request);
      void reply.catch(() => setLiveStatus('The page response could not be delivered.'));
      return;
    }
    if (part.type === 'data-lens_filtered') {
      applyChatLensFilters(part.data as unknown as LensFiltered);
      return;
    }
    if (part.type === 'data-lens_created') {
      onLensStreamedRef.current?.(part.data);
      return;
    }
    const text = statusTextOf(part);
    if (text) {
      setLiveStatus(text);
    }
  }, []);
  const createChat = useCallback(
    (messages?: Array<AskUIMessage>) => {
      const next = createAskChat({
        transport,
        messages,
        onFinish: (event) => {
          if (activeChatRef.current === next) handleFinish(event);
        },
        onData: handleData,
      });
      return next;
    },
    [handleData, handleFinish, transport],
  );
  const [chat, setChat] = useState(() => createChat());
  activeChatRef.current = chat;
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const handledNewChatRequestRef = useRef<number | null>(null);
  const handledDraftRequestRef = useRef<number | null>(null);
  const exportedOnOpenRef = useRef(false);
  const lastTurnExtra = useRef<Partial<AskTurnOptions> | undefined>(undefined);

  const { messages, status, error, stop, regenerate, setMessages } = useChat<AskUIMessage>({
    chat,
    throttle: STREAM_THROTTLE_MS,
  });
  const history = useUiStreamSessionHistory(openingSessionId);

  const working =
    status === 'submitted' ||
    status === 'streaming' ||
    (history.data?.sessionId === openingSessionId && history.awaitingReply);
  const expired = history.data?.sessionId === openingSessionId && history.expired;
  const stopAvailable = working || expired;
  const sessionId = latestSessionId(messages) ?? threadSessionId;
  const lastMessage = messages[messages.length - 1];
  const { scrollRef, showJumpToLatest, handleScroll, jumpToLatest, followLatest, hideJumpToLatest } = useFollowLatest({
    messages,
    status,
    working,
    liveStatus,
  });

  useEffect(() => {
    if (lastMessage?.role !== 'assistant' || pendingTurn === null || lastMessage.id === pendingTurn.afterMessageId) {
      return;
    }
    setPendingTurn(null);
    setTimings((prev) =>
      prev[lastMessage.id] ? prev : { ...prev, [lastMessage.id]: { startedAt: pendingTurn.startedAt } },
    );
  }, [lastMessage?.id, lastMessage?.role, pendingTurn]);

  useEffect(() => {
    onSessionChange?.(sessionId);
  }, [onSessionChange, sessionId]);

  useEffect(() => {
    onStreamingChange?.(working);
  }, [onStreamingChange, working]);

  useEffect(
    () => () => {
      void chat.stop();
    },
    [chat],
  );

  const resetThread = useCallback(
    (nextSessionId: string | null) => {
      lastTurnExtra.current = undefined;
      historyConfirmationRef.current = null;
      setConfirmingHistory(false);
      const next = createChat();
      setChat(next);
      setThreadSessionId(nextSessionId);
      setOpeningSessionId(nextSessionId);
      setLoadedHistorySessionId(null);
      setEndings({});
      setTimings({});
      setLiveStatus(null);
      setPendingSteers([]);
      setReadOnly(false);
      setLoadedSession(null);
      replaceImages([]);
      stopSpeech();
      followLatest();
      return next;
    },
    [createChat, followLatest, replaceImages, stopSpeech],
  );

  useEffect(() => {
    if (!controlledSessionId || controlledSessionId === sessionId || controlledSessionId === openingSessionId) {
      return;
    }
    resetThread(controlledSessionId);
  }, [controlledSessionId, openingSessionId, resetThread, sessionId]);

  useEffect(() => {
    const loaded = history.data;
    if (loaded?.sessionId !== openingSessionId) {
      return;
    }
    lastTurnExtra.current = undefined;
    setMessages(loaded.messages);
    recordVisibility(loaded.sessionId, loaded.visibility);
    setReadOnly(loaded.readOnly);
    setLoadedSession({
      sessionId: loaded.sessionId,
      contextChars: loaded.contextChars,
      startedAt: loaded.startedAt,
      updatedAt: loaded.updatedAt,
      messageCount: loaded.messages.length,
    });
    setLoadedHistorySessionId(loaded.sessionId);
    if (!historyAwaitsReply(loaded)) {
      setOpeningSessionId(null);
    }
  }, [history.data, openingSessionId, recordVisibility, setMessages]);

  useEffect(() => {
    if (
      !exportOnOpenSessionId ||
      exportedOnOpenRef.current ||
      loadedHistorySessionId !== exportOnOpenSessionId ||
      openingSessionId ||
      working
    ) {
      return;
    }
    const timer = window.setTimeout(() => {
      exportedOnOpenRef.current = true;
      void exportConversation(conversationMessages(messages), exportOnOpenSessionId);
    }, EXPORT_ON_OPEN_RENDER_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [exportConversation, exportOnOpenSessionId, loadedHistorySessionId, messages, openingSessionId, working]);

  const lensChatId = lensContext ? lensSaveTarget?.lensId : undefined;
  const turnOptions = useCallback(
    (extra?: Partial<AskTurnOptions>): AskTurnOptions => {
      const { context = turnContext, ...rest } = extra ?? {};
      return {
        sessionId,
        mode: lensContext || pageBridge ? 'light' : NEW_SESSION_MODE,
        origin: sessionOrigin ?? (lensContext ? 'lens_chat' : 'chat'),
        lensId: lensChatId,
        context: pageBridge
          ? [
              {
                kind: 'page_bridge',
                text: JSON.stringify({ version: 1, pageId: pageBridge.pageId, connectionId: pageBridge.connectionId }),
                applies: 'this_turn',
              },
            ]
          : lensChatId
            ? [
                ...(context ?? []).filter((block) => block.kind !== 'lens_view_filters'),
                { kind: 'lens_view_filters', text: 'v1', applies: 'this_turn' },
              ]
            : context,
        ...rest,
        ...(creationSpaceId && rest.lensIntent?.kind === 'create'
          ? { lensIntent: { ...rest.lensIntent, spaceId: creationSpaceId } }
          : {}),
      };
    },
    [pageBridge, creationSpaceId, lensChatId, lensContext, sessionId, sessionOrigin, turnContext],
  );

  const beginTurn = useCallback(() => {
    setPendingTurn({ startedAt: Date.now(), afterMessageId: lastMessage?.id });
    setOpeningSessionId(null);
    setLiveStatus(null);
    followLatest();
  }, [followLatest, lastMessage?.id]);

  const sendTurn = useCallback(
    (target: Chat<AskUIMessage>, text: string, extra?: Partial<AskTurnOptions>, files: Array<FileUIPart> = []) => {
      beginTurn();
      hideJumpToLatest();
      lastTurnExtra.current = extra;
      const questionAnswer = extra?.lensIntent?.kind === 'answer' ? extra.lensIntent.questionAnswer : undefined;
      void target.sendMessage(
        { text, files, ...(questionAnswer ? { metadata: { questionAnswer } } : {}) },
        { body: turnOptions(extra) },
      );
    },
    [beginTurn, hideJumpToLatest, turnOptions],
  );

  useEffect(() => {
    if (controlledNewChatRequestId === null || handledNewChatRequestRef.current === controlledNewChatRequestId) {
      return;
    }
    handledNewChatRequestRef.current = controlledNewChatRequestId;
    if (claimShellRequest && !claimShellRequest('new-chat', controlledNewChatRequestId)) {
      return;
    }
    resetThread(null);
    setInput('');
  }, [claimShellRequest, controlledNewChatRequestId, resetThread]);

  useEffect(() => {
    const sessionSwitchPending =
      Boolean(openingSessionId) || (Boolean(controlledSessionId) && controlledSessionId !== sessionId);
    if (
      !controlledDraft ||
      handledDraftRequestRef.current === controlledDraft.requestId ||
      lensContextPending ||
      (controlledDraft.preserveCurrentSession && sessionSwitchPending)
    ) {
      return;
    }
    handledDraftRequestRef.current = controlledDraft.requestId;
    if (claimShellRequest && !claimShellRequest('draft', controlledDraft.requestId)) {
      return;
    }
    const target = controlledDraft.preserveCurrentSession ? chat : resetThread(null);
    const draftImages = (controlledDraft.images ?? []).map(imagePartOf);
    if (!controlledDraft.autoSend) {
      setInput(controlledDraft.text);
      replaceImages(draftImages);
      return;
    }
    sendTurn(
      target,
      controlledDraft.text,
      {
        sessionId: controlledDraft.preserveCurrentSession ? sessionId : null,
        lensIntent: controlledDraft.lensIntent,
        context: [...(turnContext ?? []), ...(controlledDraft.context ?? [])],
      },
      draftImages,
    );
  }, [
    chat,
    claimShellRequest,
    controlledDraft,
    controlledSessionId,
    lensContextPending,
    openingSessionId,
    replaceImages,
    resetThread,
    sendTurn,
    sessionId,
    turnOptions,
  ]);

  const steer = useCallback(
    async (text: string) => {
      if (!sessionId) {
        enqueueSnackbar('askRichard has not opened this conversation yet. Try again in a moment, or press Stop.', {
          variant: 'warning',
        });
        setInput(text);
        return;
      }
      setPendingSteers((prev) => [...prev, text]);
      try {
        await steerSession({ sessionId, text });
      } catch (steerError) {
        setPendingSteers((prev) => prev.filter((pending) => pending !== text));
        setInput((prev) => (prev.trim() ? prev : text));
        enqueueSnackbar(
          isSteerConflict(steerError)
            ? 'That answer already finished. Send it as a new question.'
            : steerError instanceof Error
              ? steerError.message
              : 'Could not steer the current answer',
          { variant: isSteerConflict(steerError) ? 'info' : 'error' },
        );
      }
    },
    [enqueueSnackbar, sessionId, steerSession],
  );

  const turns = useMemo(
    () => messages.map((message) => (message.role === 'assistant' ? projectAssistantTurn(message) : null)),
    [messages],
  );
  const lensBlockIds = useMemo(
    () => latestLensBlockIds(turns.filter((turn): turn is AssistantTurn => turn !== null)),
    [turns],
  );
  const lastAssistantTurn = [...turns].reverse().find((turn): turn is AssistantTurn => turn !== null);
  const pendingQuestion = useMemo(() => {
    for (const turn of [...turns].reverse()) {
      for (const block of [...(turn?.blocks ?? [])].reverse()) {
        if (block.type === 'lens_draft' || block.type === 'lens_applied' || block.type === 'lens_created') {
          return undefined;
        }
        if (block.type === 'lens_scope') {
          const payload = block.payload as unknown as LensScopeBlockPayload;
          return payload.question ? payload : undefined;
        }
      }
    }
    return undefined;
  }, [turns]);

  const { refetch: refetchHistory } = history;
  const submit = useCallback(async () => {
    const text = input.trim();
    if (!text || historyConfirmationRef.current || stopping || (lensContextPending && !working)) {
      return;
    }
    let steerCurrentTurn = working;
    if (expired && !working) {
      const confirmation = Symbol();
      historyConfirmationRef.current = confirmation;
      setConfirmingHistory(true);
      try {
        const refreshed = await refetchHistory({ throwOnError: true });
        if (historyConfirmationRef.current !== confirmation) {
          return;
        }
        if (!refreshed.isSuccess || refreshed.data?.sessionId !== openingSessionId || !refreshed.data.messages.length) {
          throw new Error('Unconfirmed history');
        }
        setMessages(refreshed.data.messages);
        steerCurrentTurn = historyAwaitsReply(refreshed.data);
      } catch {
        if (historyConfirmationRef.current === confirmation) {
          enqueueSnackbar("askRichard couldn't confirm whether the earlier answer finished. Try again or press Stop.", {
            variant: 'warning',
          });
        }
        return;
      } finally {
        if (historyConfirmationRef.current === confirmation) {
          historyConfirmationRef.current = null;
          setConfirmingHistory(false);
        }
      }
    }
    if (steerCurrentTurn && images.attachments.length) {
      enqueueSnackbar('Images can be sent after askRichard finishes the current answer.', { variant: 'info' });
      return;
    }
    setInput((prev) => (prev.trim() === text ? '' : prev));
    if (steerCurrentTurn) {
      void steer(text);
      return;
    }
    const lensIntent = resolveLensIntentForTurn(text, null, {
      createEmptyState,
      sourceAnchorId: createSourceAnchorId,
      hasSession: Boolean(sessionId),
      openScopeAnswer: pendingQuestion?.question
        ? { brief: pendingQuestion.brief, questionId: pendingQuestion.question.id }
        : undefined,
    });
    sendTurn(chat, text, lensIntent ? { lensIntent } : {}, images.attachments);
    replaceImages([]);
  }, [
    chat,
    createEmptyState,
    createSourceAnchorId,
    enqueueSnackbar,
    expired,
    images.attachments,
    input,
    lensContextPending,
    openingSessionId,
    pendingQuestion,
    refetchHistory,
    replaceImages,
    sendTurn,
    sessionId,
    setMessages,
    steer,
    stopping,
    turnContext,
    working,
  ]);

  const stopTurn = useCallback(async () => {
    if (!stopAvailable || stopping) {
      return;
    }
    historyConfirmationRef.current = null;
    setConfirmingHistory(false);
    setStopping(true);
    try {
      if (sessionId) {
        await abortSession(sessionId);
        if (openingSessionId) {
          await refetchHistory();
        }
      }
    } catch (abortError) {
      if (!isAbortEndpointMissing(abortError)) {
        enqueueSnackbar(abortError instanceof Error ? abortError.message : 'Could not stop askRichard', {
          variant: 'error',
        });
      }
    } finally {
      await stop();
      setStopping(false);
      inputRef.current?.focus();
    }
  }, [abortSession, enqueueSnackbar, openingSessionId, refetchHistory, sessionId, stop, stopAvailable, stopping]);

  const failure = status === 'error' && error ? describeChatFailure(error) : null;
  const loadingHistory = Boolean(openingSessionId) && history.isPending;
  const canRetry = canRetryLatestTurn({
    messages,
    failure,
    working,
    readOnly,
    expired,
    historyPending: Boolean(openingSessionId) || loadingHistory,
    confirmingHistory,
    contextPending: lensContextPending,
  });
  const retry = useCallback(() => {
    if (!canRetry) {
      return;
    }
    beginTurn();
    setThreadSessionId(sessionId);
    const { sessionId: _sentSessionId, ...extra } = lastTurnExtra.current ?? {};
    void regenerate({ body: turnOptions(extra) });
  }, [beginTurn, canRetry, regenerate, sessionId, turnOptions]);

  const sendLensTurn = useCallback(
    (question: string, lensIntent: LensIntent) => {
      if (working || expired || confirmingHistory) {
        return;
      }
      sendTurn(chat, question, { lensIntent });
    },
    [chat, confirmingHistory, expired, sendTurn, working],
  );

  const appendDictation = useCallback((transcript: string) => {
    setInput((prev) => (prev ? `${prev} ${transcript}` : transcript));
  }, []);

  useEffect(() => {
    if (!lastAssistantTurn || pendingSteers.length === 0) {
      return;
    }
    const acknowledged = new Set(lastAssistantTurn.steers);
    if (!pendingSteers.some((steerText) => acknowledged.has(steerText))) {
      return;
    }
    setPendingSteers((prev) =>
      prev.some((steerText) => acknowledged.has(steerText))
        ? prev.filter((steerText) => !acknowledged.has(steerText))
        : prev,
    );
  }, [lastAssistantTurn, pendingSteers]);

  const awaitingFirstChunk = status === 'submitted' && lastMessage?.role !== 'assistant';
  const isEmpty = messages.length === 0 && !openingSessionId;
  const showCreateEmptyState = createEmptyState && isEmpty && !sessionId;
  const showDatasetAskIntro =
    isEmpty && Boolean(datasetContext) && !showCreateEmptyState && !cardApply && !lensContext && !readOnly;
  const statusText = liveStatus ?? RICHARD_MICROCOPY.thinkingLabel;
  const firstUser = messages.find((message) => message.role === 'user');
  const firstQuestion = firstUser ? userMessageText(firstUser) : '';
  const threadTitle =
    stripInternalSessionContext(recentSessions?.find((session) => session.sessionId === sessionId)?.title ?? '') ||
    firstQuestion;

  const retryAction = canRetry ? (
    <Button color="inherit" size="small" onClick={retry}>
      Retry
    </Button>
  ) : undefined;
  const failureAction =
    failure?.kind === 'auth' ? (
      <Button color="inherit" size="small" onClick={() => void auth.startLogin()}>
        Sign in
      </Button>
    ) : (
      retryAction
    );
  const failureFault: TurnFault | null = failure
    ? { kind: failure.kind, message: failure.message, action: failureAction }
    : null;

  const now = new Date();
  const longChat = longChatNoticeState({
    contextChars: currentContextChars(messages, loadedSession, sessionId),
    surface: onStartNewChat ? (variant === 'pane' ? 'pane' : 'page') : null,
    sessionId,
    working: working || expired,
    readOnly,
    snoozed: Boolean(sessionId) && (snoozedSessionId === sessionId || isLongChatSnoozed(sessionId ?? '', now)),
    messages,
    loaded: loadedSession,
  });
  const snoozeLongChatNotice = () => {
    if (sessionId) {
      snoozeLongChat(sessionId, new Date());
      setSnoozedSessionId(sessionId);
    }
  };
  const resumedFrom =
    variant === 'pane' && onStartNewChat
      ? resumedChatDay({
          autoResumed: sessionAutoResumed,
          sessionId,
          readOnly,
          messages,
          loaded: loadedSession,
          now,
        })
      : undefined;

  // Only a freshly appended turn animates in; a loaded or switched-in history never does.
  const entranceRef = useRef<MessageEntranceTracker | null>(null);
  const entranceIdRef = useRef<string | null>(null);
  const messageIds = useMemo(() => messages.map((message) => message.id), [messages]);
  const entrance = resolveMessageEntrance({
    messageIds,
    conversationKey: sessionId ?? (messages.length ? 'draft' : null),
    prev: entranceRef.current,
    currentEntranceId: entranceIdRef.current,
  });
  entranceRef.current = entrance.next;
  entranceIdRef.current = entrance.entranceId;

  const lastSettled =
    !working && !expired && lastMessage?.role === 'assistant' && turns[messages.length - 1]?.hasAnswerText === true;
  const followUps =
    variant === 'pane' && lastSettled && !input.trim()
      ? suggestedFollowUps({ cardTitle: subjectChip, lensScoped: Boolean(lensContext) })
      : [];
  const composerScope: ComposerScope | undefined = subjectChip
    ? { label: subjectChip, lens: variant === 'pane', onClear: variant === 'pane' ? onClearScope : undefined }
    : undefined;

  const composer = (
    <UiStreamComposer
      variant={variant}
      value={input}
      onChange={setInput}
      onSubmit={submit}
      onStop={() => void stopTurn()}
      onDictated={appendDictation}
      attachments={images.attachments}
      attachmentsReading={images.reading}
      onAttach={images.add}
      onRemoveAttachment={images.remove}
      working={working || expired}
      stopAvailable={stopAvailable}
      canSteer={Boolean(sessionId)}
      hasThread={messages.length > 0}
      inputRef={inputRef}
      idlePlaceholder={composerPlaceholder ?? (datasetContext ? `Ask about ${datasetContext.name}…` : undefined)}
      sendBlocked={(lensContextPending && !working) || confirmingHistory || stopping}
      scope={composerScope}
      examples={
        followUps.length > 0 ? (
          <FollowUps questions={followUps} onPick={(question) => sendTurn(chat, question)} />
        ) : undefined
      }
    />
  );

  const lens: LensHandlers = {
    latestLensScopeBlockId: lensBlockIds.scope,
    latestLensCreatedBlockId: latestCreatedReceiptId(turns.filter((turn): turn is AssistantTurn => turn !== null)),
    lensTurnSending: working || expired || confirmingHistory,
    onSendLensTurn: sendLensTurn,
    onLensCreated,
  };

  const turnFault = (message: AskUIMessage, turn: AssistantTurn, isLast: boolean): TurnFault | null => {
    if (endings[message.id] === 'stopped' || turn.code === 'stopped') {
      return { kind: 'stopped', message: STOPPED_MESSAGE };
    }
    if (isLast && failureFault) {
      return failureFault;
    }
    if (turn.errorText) {
      return { kind: 'server', message: turn.errorText, action: isLast ? retryAction : undefined };
    }
    if (endings[message.id] === 'cut-off') {
      return { kind: 'cut-off', message: CUT_OFF_MESSAGE };
    }
    if (isLast && expired) {
      return { kind: 'cut-off', message: HISTORY_EXPIRED_MESSAGE };
    }
    return null;
  };

  const turnStatus = (fault: TurnFault | null, isLast: boolean): TurnStatus => {
    if (isLast && working) {
      return 'live';
    }
    if (fault?.kind === 'stopped') {
      return 'stopped';
    }
    return fault ? 'failed' : 'complete';
  };

  const renderAssistant = (message: AskUIMessage, turn: AssistantTurn, index: number) => {
    const isLast = index === messages.length - 1;
    const precedingUser = messages
      .slice(0, index)
      .reverse()
      .find((candidate) => candidate.role === 'user');
    const reply = messages.slice(index + 1).find((candidate) => candidate.role === 'user');
    const timing = timings[message.id];
    const startedAtMs = timing?.startedAt ?? parseStamp(turn.startedAt ?? precedingUser?.metadata?.createdAt);
    const finishedAtMs = timing?.finishedAt ?? parseStamp(turn.finishedAt ?? message.metadata?.createdAt);
    const fault = turnFault(message, turn, isLast);
    return (
      <AssistantTurnView
        key={message.id}
        turn={turn}
        status={turnStatus(fault, isLast)}
        statusText={statusText}
        pendingSteers={pendingSteers}
        sourceQuestion={precedingUser ? userMessageText(precedingUser) : undefined}
        replyText={reply ? userMessageText(reply) : undefined}
        replyQuestionAnswer={reply?.metadata?.questionAnswer}
        fault={fault}
        debug={debug}
        elapsedMs={startedAtMs !== undefined && finishedAtMs !== undefined ? finishedAtMs - startedAtMs : undefined}
        lens={lens}
        prominentLensSave={prominentLensSave}
        compactSkeleton
        showAnswerActions={sessionOrigin !== 'lens_create'}
        speech={speech}
        dashboardDestinations={dashboardDestinations}
        onSavedLens={onSavedLens}
        onBuildDashboard={onBuildDashboard}
        lensSaveTarget={lensSaveTarget}
        allowGlobalLensDrafts={threadView}
        forcedReplacementCard={forcedReplacementCard}
        cardApply={cardApply}
      />
    );
  };

  return (
    <Box
      data-testid="ui-stream-chat"
      data-wire="ui-message-stream"
      data-chat-status={status}
      data-session-id={sessionId ?? ''}
      data-history-session={loadedHistorySessionId ?? ''}
      data-debug={debug || undefined}
      sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}
    >
      {resumedFrom && onStartNewChat ? (
        <Typography
          color="text.secondary"
          data-testid="resumed-chat-caption"
          sx={{ flex: 'none', fontSize: 12, px: 2.5, pt: 1 }}
        >
          Continuing your chat from {resumedFrom} ·{' '}
          <Link component="button" onClick={onStartNewChat} sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}>
            Start new chat
          </Link>
        </Typography>
      ) : null}
      {pageBridge || (threadView && messages.length > 0) ? (
        <ThreadBar
          title={threadTitle}
          compact={Boolean(pageBridge)}
          actions={toolbarActions}
          leadingAction={toolbarLeadingAction}
          sessionId={sessionId}
          visibility={(sessionId && visibilityBySession[sessionId]) || 'private'}
          onVisibilityChanged={recordVisibility}
          working={working}
          exporting={exporting}
          onExport={() => void exportConversation(conversationMessages(messages), sessionId)}
        />
      ) : null}
      {historyPanel && (
        <Box
          sx={{
            position: 'absolute',
            top: 44,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 2,
            bgcolor: 'background.paper',
            display: 'flex',
          }}
        >
          {historyPanel}
        </Box>
      )}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          visibility: historyPanel ? 'hidden' : undefined,
          overscrollBehavior: pageBridge ? 'contain' : undefined,
          px: variant === 'pane' ? 2.5 : { xs: 1.5, md: 3 },
          pt: variant === 'pane' ? 2.5 : 4,
          pb: 2,
          position: 'relative',
        }}
      >
        <Stack spacing={3.5}>
          {showCreateEmptyState ? (
            <LensCreateEmptyState
              composerSlot={composer}
              onOpenLens={onOpenLens}
              onPickBrief={(brief: string, mode: LensPlanMode) =>
                sendLensTurn(brief, {
                  brief,
                  kind: 'create',
                  mode,
                  ...(createSourceAnchorId ? { sourceAnchorId: createSourceAnchorId } : {}),
                })
              }
              onResumeSession={onResumeCreateSession}
              resumableSessions={createResumeSessions}
            />
          ) : isEmpty && emptyState !== undefined ? (
            emptyState
          ) : isEmpty && (cardApply || lensContext || pageBridge) ? (
            <CardAskEmptyState
              cardTitle={subjectChip}
              lensTitle={lensContext?.title}
              onAsk={(question) => sendTurn(chat, question)}
              onDraft={(text) => {
                setInput(text);
                inputRef.current?.focus();
              }}
            />
          ) : showDatasetAskIntro && datasetContext ? (
            <DatasetAskIntro
              composerSlot={composer}
              dataset={datasetContext}
              onAsk={(question) => sendTurn(chat, question)}
              onBack={onBackToDataset}
            />
          ) : isEmpty && !lensContext ? (
            <EmptyState onAsk={(question) => sendTurn(chat, question)} />
          ) : null}
          {loadingHistory ? (
            <Stack spacing={1.5} data-testid="ui-stream-history-loading">
              <Skeleton variant="rounded" height={40} sx={{ ml: 'auto', width: '45%' }} />
              <Skeleton variant="rounded" height={180} />
            </Stack>
          ) : null}
          {history.isError && openingSessionId ? (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={() => void history.refetch()}>
                  Retry
                </Button>
              }
            >
              {history.error.message}
            </Alert>
          ) : null}
          {messages.length > 0 ? (
            <Box
              data-testid="ui-stream-thread"
              sx={{
                position: 'relative',
                // The spine: one line through the centre of every avatar and marker ring.
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: SPINE_X - SPINE_WIDTH / 2,
                  top: 12,
                  bottom: 8,
                  width: SPINE_WIDTH,
                  bgcolor: pane.spine,
                  borderRadius: 1,
                },
              }}
            >
              <Stack spacing={3.5} sx={{ position: 'relative' }}>
                {messages.map((message, index) => {
                  if (
                    message.role === 'user' &&
                    message.metadata?.questionAnswer?.optionIds?.length &&
                    !message.metadata.questionAnswer.text?.trim()
                  ) {
                    return null;
                  }
                  const turn = turns[index];
                  const isFirstUser = message.role === 'user' && messages.findIndex((m) => m.role === 'user') === index;
                  const entranceSx = messageEntranceSx(entrance.entranceId === message.id);
                  if (message.role === 'user' || !turn) {
                    return (
                      <Box
                        key={message.id}
                        sx={{
                          position: 'relative',
                          pl: `${SPINE_X + 20}px`,
                          // The marker ring, centred on the spine, level with the first line of the bubble.
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: SPINE_X - SPINE_MARKER / 2,
                            top: 13,
                            width: SPINE_MARKER,
                            height: SPINE_MARKER,
                            boxSizing: 'border-box',
                            borderRadius: '50%',
                            border: `2px solid ${pane.accent}`,
                            bgcolor: 'background.paper',
                          },
                          ...entranceSx,
                        }}
                      >
                        <UserTurn
                          message={message}
                          evidence={isFirstUser ? firstTurnEvidence : undefined}
                          variant={variant}
                        />
                      </Box>
                    );
                  }
                  return (
                    <Box key={message.id} sx={entranceSx}>
                      {renderAssistant(message, turn, index)}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ) : null}
          {expired && lastMessage?.role === 'user' ? (
            <TurnFaultAlert fault={{ kind: 'cut-off', message: HISTORY_EXPIRED_MESSAGE }} />
          ) : null}
          {awaitingFirstChunk ? (
            <AssistantTurnView
              compactSkeleton
              turn={PENDING_TURN}
              status="live"
              statusText={statusText}
              pendingSteers={pendingSteers}
              fault={null}
              debug={false}
              lens={lens}
              prominentLensSave={prominentLensSave}
            />
          ) : null}
          {failureFault && lastMessage?.role !== 'assistant' ? <TurnFaultAlert fault={failureFault} /> : null}
        </Stack>
        {showJumpToLatest ? <JumpToLatestButton onClick={jumpToLatest} /> : null}
      </Box>
      {showCreateEmptyState || showDatasetAskIntro ? null : (
        <Box sx={{ px: variant === 'pane' ? 2.5 : { xs: 1.5, md: 3 }, pb: 2, pt: 1 }}>
          <Box>
            {onStartNewChat ? (
              <LongChatNotice state={longChat} onNotNow={snoozeLongChatNotice} onStartNewChat={onStartNewChat} />
            ) : null}
            {readOnly ? (
              <Alert
                severity="info"
                data-testid="ui-stream-read-only"
                action={
                  onContinueInNewChat ? (
                    <Button color="inherit" size="small" onClick={() => onContinueInNewChat(firstQuestion)}>
                      {firstQuestion ? 'Continue in a new chat' : 'New chat'}
                    </Button>
                  ) : undefined
                }
              >
                {RICHARD_MICROCOPY.readOnlySessionNotice}
              </Alert>
            ) : (
              composer
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
